import { Signal } from '@crown/signal';
import { EmptyState } from '@crown/ui/empty-state';
import { Tabs } from '@crown/ui/tabs';
import { type DeploymentReference, deploymentReferences, diagramCards, type MermaidSource, mermaidSources } from '../content/content-manifest';
import { initDiagramZoom } from '../diagrams/diagram-zoom';
import type { ActionLink } from '../page-elements';
import { ActionRow, DocsCard, PillRow } from '../page-elements.tsrx';

function DeploymentPanel({ reference }: { readonly reference: DeploymentReference }) @{
  const actions: ActionLink[] = [
    { label: 'Open notes', href: `/${reference.routePath}`, variant: 'primary' },
    { label: 'Open diagram', href: reference.diagramHref, newTab: true },
    ...(reference.flowHref ? [{ label: 'Open flow', href: reference.flowHref, newTab: true }] : []),
  ];
  <div class="docs-tab-panel" ref={(panel: Element) => initDiagramZoom(panel)}>
    <div class="docs-diagram-preview">
      <img src={reference.diagramHref} alt={reference.title} loading="lazy" />
    </div>
    <div class="docs-tab-copy">
      <PillRow tags={reference.tags} />
      <h3>{reference.title}</h3>
      <p class="docs-section-copy">{reference.summary}</p>
      <ul class="docs-bullet-list">
        @for (const item of reference.highlights; key item) {
          <li>{item}</li>
        }
      </ul>
      <ActionRow actions={actions} />
    </div>
  </div>
}

type Preview = { readonly source: MermaidSource; readonly state: 'loading' | 'ready' | 'failed' } | null;

function matches(source: MermaidSource, query: string): boolean {
  const q = query.toLowerCase().trim();
  return !q || source.title.toLowerCase().includes(q) || source.tags.some((tag) => tag.toLowerCase().includes(q)) || source.id.includes(q);
}

function MermaidExplorer() @{
  const query = new Signal.State('');
  const preview = new Signal.State<Preview>(null);
  const shown = (): readonly MermaidSource[] => mermaidSources.filter((source) => matches(source, query.get()));
  const select = (source: MermaidSource): void => {
    if (preview.get()?.source.id !== source.id) preview.set({ source, state: 'loading' });
  };
  const settle = (state: 'ready' | 'failed'): void => {
    const current = preview.get();
    if (current) preview.set({ source: current.source, state });
  };
  <section class="docs-section" aria-labelledby="mermaid-heading">
    <div class="docs-section-heading">
      <div>
        <h2 id="mermaid-heading">Mermaid source explorer</h2>
        <p class="docs-section-copy">
          {`${mermaidSources.length} generated diagram sources. View the raw `}
          <code>.mmd</code>
          {', rendered SVG, or choose one to preview it here.'}
        </p>
      </div>
    </div>
    <div class="docs-mmd-search-row">
      <input type="search" class="docs-mmd-search" placeholder="Filter diagrams…" aria-label="Filter diagrams" onInput={(event: Event) => query.set((event.currentTarget as HTMLInputElement).value)} />
      <span class="docs-mmd-count" role="status">{query.get().trim() ? `${shown().length} / ${mermaidSources.length}` : `${mermaidSources.length} sources`}</span>
    </div>
    <div class="docs-mmd-explorer">
      <ul class="docs-mmd-list">
        @for (const source of shown(); key source.id) {
          <li class={preview.get()?.source.id === source.id ? 'docs-mmd-row docs-mmd-row--active' : 'docs-mmd-row'} data-id={source.id}>
            <button type="button" class="docs-mmd-row-info" aria-pressed={preview.get()?.source.id === source.id} onClick={() => select(source)}>
              <strong>{source.title}</strong>
              <span>{source.description}</span>
            </button>
            <div class="docs-mmd-row-tags">
              @for (const tag of source.tags; key tag) {
                <span class="docs-pill">{tag}</span>
              }
            </div>
            <div class="docs-mmd-row-links">
              <a href={source.mmdHref} target="_blank" rel="noopener" class="docs-mmd-link" aria-label={`${source.title} source (.mmd)`}>.mmd</a>
              <a href={source.svgHref} target="_blank" rel="noopener" class="docs-mmd-link" aria-label={`${source.title} as SVG`}>.svg</a>
              <a href={source.pngHref} target="_blank" rel="noopener" class="docs-mmd-link" aria-label={`${source.title} as PNG`}>.png</a>
            </div>
          </li>
        }
      </ul>
      <div class="docs-mmd-preview" aria-live="polite">
        @if (!preview.get()) {
          <>{EmptyState({ title: 'Select a diagram', description: 'Choose any row to preview its rendered SVG.', variant: 'compact' })}</>
        } @else {
          <>
            @if (preview.get()?.state === 'loading') {
              <>{EmptyState({ title: 'Loading SVG…', variant: 'compact' })}</>
            } @else if (preview.get()?.state === 'failed') {
              <>{EmptyState({ title: 'Failed to load SVG', description: preview.get()?.source.title ?? '', variant: 'compact' })}</>
            } @else {
              <span class="docs-mmd-preview-label">{preview.get()?.source.title ?? ''}</span>
            }
            <img class="docs-mmd-preview-img" src={preview.get()?.source.svgHref ?? ''} alt={preview.get()?.source.title ?? ''} hidden={preview.get()?.state !== 'ready'} onLoad={() => settle('ready')} onError={() => settle('failed')} />
          </>
        }
      </div>
    </div>
  </section>
}

export default function ArchitectureHub() @{
  const active = new Signal.State(deploymentReferences[0]?.id ?? '');
  <div class="docs-page">
    <section class="docs-hero">
      <span class="docs-eyebrow">Architecture hub</span>
      <h1>Curated deployment references plus the generated diagrams that explain the runtime reality.</h1>
      <p>Use the tabs for the three deployment models, then drop into the individual notes for the full operating guidance and generated flow sections.</p>
      <ActionRow actions={[
        { label: 'Open explorer', href: '/explorer/', variant: 'primary' },
        { label: 'Deployment overview', href: '/architecture/deployment-architecture' },
        { label: 'Auth runtime', href: '/architecture/generated/auth-runtime' },
      ]} />
    </section>

    <section class="docs-section" aria-labelledby="deployment-models">
      <div class="docs-section-heading">
        <div>
          <h2 id="deployment-models">Reference deployment models</h2>
          <p class="docs-section-copy">Same platform, different operational trade-offs. Pick your favourite controlled amount of chaos.</p>
        </div>
      </div>
      <div class="docs-tabs">
        {Tabs({
          label: 'Deployment models',
          active,
          tabs: () => deploymentReferences.map((reference) => ({ id: reference.id, label: reference.title, disabled: false })),
          panel: (id) => {
            const reference = deploymentReferences.find((candidate) => candidate.id === id);
            return reference ? <DeploymentPanel reference={reference} /> : null;
          },
        })}
      </div>
    </section>

    <section class="docs-section" aria-labelledby="runtime-views">
      <div class="docs-section-heading">
        <div>
          <h2 id="runtime-views">Generated runtime views</h2>
          <p class="docs-section-copy">These cards link straight to the generated assets while keeping the related written notes one click away.</p>
        </div>
      </div>
      <div class="docs-card-grid">
        @for (const card of diagramCards; key card.id) {
          <DocsCard
            eyebrow="Generated"
            title={card.title}
            description={card.description}
            tags={card.tags}
            actions={[{ label: 'Open asset', href: card.href, newTab: true, variant: 'primary' }, ...(card.routePath ? [{ label: 'Related notes', href: `/${card.routePath}` }] : [])]}
          />
        }
      </div>
    </section>

    <MermaidExplorer />
  </div>
}
